<script setup lang="ts">
import Modal from "@/ui/components/modal.vue";
import MasterPriceDO = Modules.BookingExperts.Data.MasterPriceList.MasterPriceDO;
import { computed, ref, watch } from "vue";
import { formatNumber } from "chartjs-graph";
import { t } from "@/plugins/i18n";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import { useBaseStore } from "@/stores/base";

const ALL = "all" as const;

const isOpen = defineModel<boolean>("isOpen");

const { selectedId } = defineProps<{
	selectedId: number | null;
}>();

const emit = defineEmits(["refresh"]);

const api = useApiClient("bookingExperts");
const store = useBaseStore();

// State
const allPriceLists = ref<MasterPriceDO[]>([]);
const loading = ref(false);
const selected = ref<number[]>([]);
const search = ref("");
const selectedAdministrationId = ref<number | typeof ALL>(ALL);

// Fetch every pricelist across all administrations (no administration filter),
// so curves can be copied to pricelists in other administrations too.
async function fetchPriceLists(): Promise<void> {
	loading.value = true;
	const result = await api.getMasterPrices();
	if (result.isOk()) {
		allPriceLists.value = result.value;
	}
	loading.value = false;
}

// Reset and (re)load whenever the modal is opened, defaulting the
// administration filter to the one currently selected on the page.
watch(
	() => isOpen.value,
	(open) => {
		if (open) {
			selected.value = [];
			search.value = "";
			selectedAdministrationId.value =
				store.selectedBexAdministration?.id ?? ALL;
			fetchPriceLists();
		}
	}
);

const managedPriceLists = computed<Record<string, MasterPriceDO>>(() =>
	allPriceLists.value.reduce((acc, item) => {
		if (item.managing_price_list_id) {
			acc[item.managing_price_list_id] = item;
		}
		return acc;
	}, {} as Record<string, MasterPriceDO>)
);

const sourcePriceLists = computed(() =>
	allPriceLists.value.filter((item) => item.is_source)
);

const curveCount = computed(() =>
	selectedId === null
		? 0
		: allPriceLists.value.find(({ id }) => id === selectedId)?.curve_count ?? 0
);

const showAdministrationFilter = computed(
	() => store.bexAdministrations.length > 1
);

const administrationItems = computed(() => [
	{ id: ALL, name: "All administrations" },
	...store.bexAdministrations,
]);

function administrationName(administrationId: number | null): string {
	return (
		store.bexAdministrations.find((a) => a.id === administrationId)?.name ?? "-"
	);
}

// When searching, search across all administrations; otherwise scope to the
// selected administration. The source pricelist itself is always excluded.
const displayedPriceLists = computed(() => {
	const base = sourcePriceLists.value.filter(({ id }) => id !== selectedId);

	if (search.value.trim() || selectedAdministrationId.value === ALL) {
		return base;
	}

	return base.filter(
		(item) => item.administration_id === selectedAdministrationId.value
	);
});

const headers = computed(() => [
	{ title: "Name", key: "name" },
	...(showAdministrationFilter.value
		? [{ title: "Administration", key: "administration", sortable: false }]
		: []),
	{ title: "Active Curves", key: "curve_count", align: "end" as const },
]);

async function applyCopy(): Promise<void> {
	if (selectedId === null) return;

	const result = await toastApiResult(
		api.copyCurves(selectedId, { priceListIds: selected.value })
	);
	if (result.isOk()) {
		emit("refresh");
		selected.value = [];
	}
}
</script>

<template>
	<Modal
		v-model:visible="isOpen"
		:title="`Copy ${curveCount} curve${
			curveCount === 1 ? '' : 's'
		} to other pricelists`"
		icon="fa fa-regular fa-clone"
		:allow-submit="selected.length > 0"
		@submit="applyCopy"
	>
		<div class="d-flex flex-column ga-4">
			<div class="flex align-center font-sm">
				<v-icon
					icon="fa: fa-light fa-circle-info"
					:size="14"
					style="margin-bottom: 2px"
					class="mr-1"
				/>
				Any active curves on the selected pricelists will be overwritten
			</div>
			<div class="d-flex flex-column flex-sm-row ga-3">
				<v-select
					v-if="showAdministrationFilter"
					v-model="selectedAdministrationId"
					:items="administrationItems"
					item-title="name"
					item-value="id"
					label="Administration"
					hide-details
					style="max-width: 240px"
				/>
				<v-text-field
					v-model="search"
					placeholder="Search"
					prepend-inner-icon="mdi-magnify"
					hide-details
				/>
			</div>
			<div v-if="selected.length" class="font-sm text-medium-emphasis">
				{{ selected.length }} pricelist{{ selected.length === 1 ? "" : "s" }}
				selected
			</div>
			<v-data-table
				v-model="selected"
				show-select
				:item-value="({ id }) => id"
				:headers="headers"
				:items="displayedPriceLists"
				:search="search"
				:loading="loading"
				class="my-n2"
				density="comfortable"
			>
				<template v-slot:item.name="{ item }">
					<div class="d-flex align-center name font-weight-medium w-100">
						<div
							v-tooltip="
								t(
									`pricelist.index.table.tooltips.${
										managedPriceLists[item.id.toString()]
											? 'managed'
											: 'unmanaged'
									}`
								)
							"
							class="chit d-inline"
							:style="{
								backgroundColor: managedPriceLists[item.id.toString()]
									? '#6ec957'
									: '#E66161',
							}"
						/>
						{{ item.name }}
					</div>
				</template>
				<template v-slot:item.administration="{ item }">
					{{ administrationName(item.administration_id) }}
				</template>
				<template #item.curve_count="{ item }">
					<v-chip
						:color="item.curve_count ? 'warning' : 'grey'"
						variant="tonal"
						density="comfortable"
						class="px-2 number-mono"
					>
						{{ formatNumber(item.curve_count) }}
					</v-chip>
				</template>
			</v-data-table>
		</div>
	</Modal>
</template>

<style scoped>
.chit {
	min-width: 10px;
	min-height: 10px;
	width: 10px;
	height: 10px;
	border-radius: 50%;
}
</style>
